import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Calendar,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "Attendance Calendar", path: "/attendance/apply-calendar", desc: "View or mark daily attendance" },
  { label: "Attendance Records", path: "/attendance/records", desc: "Review attendance record table" },
  { label: "Mark Attendance", path: "/attendance/mark", desc: "Mark attendance for a single employee" },
  { label: "Import Attendance", path: "/attendance/apply-calendar?tab=import", desc: "Upload attendance via Excel/CSV" },
  { label: "Attendance Exceptions", path: "/attendance/exceptions", desc: "Review flagged attendance exceptions" },
  { label: "Attendance Dashboard", path: "/attendance/dashboard", desc: "Overview of attendance status" },
];

const ATTENDANCE_TROUBLESHOOTING = [
  {
    problem: "Attendance not showing for an employee on a specific date",
    check: "No attendance record has been created for that employee and date. Attendance must be explicitly marked or imported — the system does not auto-generate records for missing dates.",
    action: "Go to Mark Attendance and create a record for the employee and date. Alternatively, include the entry in an import file and upload it via Import Attendance.",
    path: "/attendance/mark",
    pathLabel: "Mark Attendance",
  },
  {
    problem: "Attendance import file rejected — \"Only .csv and .xlsx files are supported\"",
    check: "The uploaded file is not in a supported format. BrixiGo accepts only .csv and .xlsx files for attendance import.",
    action: "Save your file as .csv or .xlsx before uploading. Other formats such as .xls, .pdf, or .txt are not supported. Open Import Attendance and re-upload the corrected file.",
    path: "/attendance/apply-calendar?tab=import",
    pathLabel: "Import Attendance",
  },
  {
    problem: "Attendance import shows validation errors for employee codes",
    check: "The employee_code values in the import file do not match any employee in your BrixiGo organisation, or the employee_code column is missing or has blank values.",
    action: "Verify that each employee code in your file matches the employee code in BrixiGo (check for typos or extra spaces). Ensure the column is named 'employee_code'. Download the import template for the correct format.",
    path: "/attendance/apply-calendar?tab=import",
    pathLabel: "Import Attendance",
  },
  {
    problem: "Attendance import shows \"Invalid work_date\" errors",
    check: "One or more date values in the import file could not be parsed. The system expects dates in a recognisable format.",
    action: "Use the format YYYY-MM-DD (e.g. 2026-03-01) for all dates in your file. Other accepted formats include DD/MM/YYYY and MM/DD/YYYY. Re-upload the corrected file.",
    path: "/attendance/apply-calendar?tab=import",
    pathLabel: "Import Attendance",
  },
  {
    problem: "Attendance import completed but no records were saved",
    check: "The import processed but all rows had validation errors, so no records were actually saved.",
    action: "Review the error details shown after upload. Common causes include unrecognised employee codes, invalid dates, or missing required columns. Fix the issues in your spreadsheet and re-upload.",
    path: "/attendance/apply-calendar?tab=import",
    pathLabel: "Import Attendance",
  },
  {
    problem: "Payroll shows missing attendance data for the pay period",
    check: "Attendance records do not exist for some or all employees within the selected pay period date range. Payroll pulls attendance data automatically, but the records must exist first.",
    action: "Go to the Attendance Calendar or Import Attendance and ensure all employees have attendance marked for every working day in the pay period. Then retry processing payroll.",
    path: "/attendance/apply-calendar",
    pathLabel: "Attendance Calendar",
  },
  {
    problem: "Holidays or week-offs not appearing in the attendance calendar",
    check: "Holidays and weekly offs must be applied to the calendar using the Apply Calendar function. They are not marked automatically unless this step has been completed.",
    action: "Open the Attendance Calendar, switch to the Apply tab, and apply your configured holiday calendar and weekly off settings to the relevant month.",
    path: "/attendance/apply-calendar",
    pathLabel: "Attendance Calendar",
  },
  {
    problem: "Employee cannot view their own attendance",
    check: "The My Attendance section is available for employees in self-service mode. If the employee cannot access it, verify that their role is set to 'employee' and that attendance records exist for the relevant dates.",
    action: "Ask the employee to navigate to My Attendance. If the page is not accessible, check the employee's role and page access settings in Settings.",
    path: "/my-attendance",
    pathLabel: "My Attendance",
  },
  {
    problem: "Attendance exception flagged for an employee",
    check: "An attendance exception indicates a discrepancy such as a missing punch, late arrival, or policy violation detected by the attendance policy engine.",
    action: "Go to Attendance Exceptions to review the flagged records. Resolve each exception by approving, rejecting, or correcting the attendance record.",
    path: "/attendance/exceptions",
    pathLabel: "Attendance Exceptions",
  },
];

export default function AttendanceSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return ATTENDANCE_TROUBLESHOOTING;
    return ATTENDANCE_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-green">
              <Calendar size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Attendance Issues</h1>
              <p className="hs-hero-sub">
                Troubleshoot attendance marking, import, and attendance record problems.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search attendance issues — e.g. import, missing, exception..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Attendance Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Attendance Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No attendance issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed attendance information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed attendance information?</h2>
              <p className="sc-info-row-desc">
                Visit the Attendance Help Guide for detailed documentation, FAQs and step-by-step attendance instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/attendance")}
            >
              Open Attendance Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
